import { getJson, sendJson } from '@/lib/api';

/**
 * Client types + fetcher for the Company Analyst reports. Mirrors the server
 * contract (api/v3/reports/report-types.ts): deterministic metrics plus the AI
 * analysis on top. The page renders this JSON — the LLM never produces the
 * numbers, only the analysis.
 */

export const REPORT_GRAINS = ['day', 'week', 'month', 'quarter'] as const;
export type ReportGrain = (typeof REPORT_GRAINS)[number];

export const GRAIN_LABELS: Readonly<Record<ReportGrain, string>> = {
  day: 'Daily',
  week: 'Weekly',
  month: 'Monthly',
  quarter: 'Quarterly',
};

export type MetricFormat = 'count' | 'currency' | 'percent' | 'position';
export type SectionStatus = 'ok' | 'pending' | 'empty';
export type AnalysisSeverity = 'good' | 'watch' | 'bad';

export interface KpiDelta {
  readonly pct: number | null;
  readonly direction: 'up' | 'down' | 'flat';
  readonly good: boolean;
  readonly label: string;
}
export interface Kpi {
  readonly key: string;
  readonly label: string;
  readonly value: number;
  readonly display: string;
  readonly sub?: string;
  readonly delta?: KpiDelta | null;
}
export interface Series {
  readonly key: string;
  readonly label: string;
  readonly periods: readonly string[];
  readonly values: readonly number[];
  readonly yoy?: readonly number[] | null;
  readonly format: MetricFormat;
}
export interface ReportTable {
  readonly key: string;
  readonly title: string;
  readonly columns: readonly string[];
  readonly rows: readonly (readonly string[])[];
}
export interface ReportSection {
  readonly key: string;
  readonly title: string;
  readonly status: SectionStatus;
  readonly note?: string;
  readonly kpis: readonly Kpi[];
  readonly series?: readonly Series[];
  readonly tables?: readonly ReportTable[];
}
export interface ReportMetrics {
  readonly reportType: string;
  readonly grain: ReportGrain;
  readonly period: { readonly label: string; readonly from: string; readonly to: string };
  readonly generatedAt: string;
  readonly freshness: readonly string[];
  readonly sections: readonly ReportSection[];
}

export interface AnalysisCallout {
  readonly title: string;
  readonly severity: AnalysisSeverity;
  readonly domain: string;
  readonly detail: string;
  readonly metricRefs: readonly string[];
  readonly recommendedAction: string | null;
}
export interface AnalysisAnomaly {
  readonly title: string;
  readonly severity: AnalysisSeverity;
  readonly evidence: string;
  readonly likelyCause: string | null;
  readonly suggestedInvestigation: string | null;
}
export interface AnalysisTrend {
  readonly title: string;
  readonly direction: 'up' | 'down' | 'flat';
  readonly momentum: string;
  readonly detail: string;
}
export interface AnalysisReport {
  readonly headline: string;
  readonly overallVerdict: 'strong' | 'steady' | 'soft' | 'concern';
  readonly callouts: readonly AnalysisCallout[];
  readonly anomalies: readonly AnalysisAnomaly[];
  readonly trends: readonly AnalysisTrend[];
  readonly sectionSummaries: readonly { readonly section: string; readonly summary: string }[];
  readonly emailSubject: string;
  readonly emailBullets: readonly string[];
  readonly generatedAt: string;
  readonly model: string;
}

export interface StoredReport {
  readonly metrics: ReportMetrics;
  readonly analysis: AnalysisReport | null;
}

export interface LatestReports {
  readonly reportType: string;
  readonly byGrain: Readonly<Record<ReportGrain, StoredReport | null>>;
}

/** The latest stored report of each grain (one round-trip for the toggle). */
export function readLatestReports(signal?: AbortSignal): Promise<LatestReports> {
  return getJson<LatestReports>('/reports/latest', undefined, signal);
}

/** One archived period entry, for the look-back selector. */
export interface ReportPeriodEntry {
  readonly periodStart: string;
  readonly label: string;
  readonly generatedAt: string;
  readonly hasAnalysis: boolean;
}

/** The stored periods for a grain, newest first — the archive index. */
export function readReportHistory(
  grain: ReportGrain,
  signal?: AbortSignal,
): Promise<{ reportType: string; grain: ReportGrain; periods: ReportPeriodEntry[] }> {
  return getJson('/reports/history', { grain }, signal);
}

/** One stored report by grain + period start; omit the period for the latest. */
export function readReportByPeriod(
  grain: ReportGrain,
  periodStart: string | null,
  signal?: AbortSignal,
): Promise<StoredReport | null> {
  return getJson<StoredReport | null>(
    '/reports',
    { grain, ...(periodStart ? { period: periodStart } : {}) },
    signal,
  );
}

/** Emails one stored report to stakeholders as a PDF attachment. */
export function sendReport(body: {
  grain: ReportGrain;
  period?: string;
  recipients: string[];
  note?: string;
}): Promise<{ sent: boolean; recipients: number; period: string }> {
  return sendJson('post', '/reports/send', body);
}
